import { useCallback, useMemo, useState } from "react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { useBrandApi } from "@/v2/entities/brand/Brand.api";
import { TenantBrand } from "@/v2/entities/brand/Brand.model";

interface DeleteBrandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brand: TenantBrand;
  refetch: () => void;
}

export function DeleteBrandDialog({
  open,
  onOpenChange,
  brand,
  refetch,
}: DeleteBrandDialogProps) {
  const brandApi = useBrandApi();

  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = useCallback(async () => {
    setIsLoading(true);
    try {
      await brandApi.deleteBrand({ externalBrandId: brand.externalId });
      onOpenChange(false);
      refetch();
    } catch (error) {
      // error is handled by the mutation
      // eslint-disable-next-line no-console
      console.error("failed to delete brand:", error);
    } finally {
      setIsLoading(false);
    }
  }, [brandApi, brand.externalId, onOpenChange, refetch, setIsLoading]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>Delete Brand</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Are you sure you want to delete this brand? This action cannot be
          undone.
        </Dialog.Description>

        <Flex direction="column" gap="3" mb="4">
          <div className="p-3 bg-gray-50 rounded-md">
            <Text size="2" weight="bold" mb="1">
              {brand.name}
            </Text>
            <Text size="1" color="gray" className="block">
              Subdomain: {brand.subdomain}
            </Text>
            <Text size="1" color="gray" className="block font-mono">
              External ID: {brand.externalId}
            </Text>
          </div>
        </Flex>

        <Flex gap="3" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" onClick={handleCancel}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button color="red" onClick={handleDelete} loading={isLoading}>
            Delete Brand
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
